<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from "vue";
import { AlertTriangle, Trash2 } from "lucide-vue-next";
import type {
  TaskDeletionBlocker,
  TaskDeletionCandidate,
} from "@/utils/task-deletion";

const props = withDefaults(
  defineProps<{
    open: boolean;
    tasks: TaskDeletionCandidate[];
    blockers?: TaskDeletionBlocker[];
    deleting?: boolean;
    error?: string;
    hasUnsavedChanges?: boolean;
  }>(),
  {
    deleting: false,
    error: "",
    hasUnsavedChanges: false,
    blockers: () => [],
  },
);
const emit = defineEmits<{
  cancel: [];
  confirm: [];
}>();

const confirmation = ref("");
const dialog = ref<HTMLFormElement | null>(null);
const confirmationInput = ref<HTMLInputElement | null>(null);
const cancelButton = ref<HTMLButtonElement | null>(null);
let opener: HTMLElement | null = null;
let priorBodyOverflow = "";
const hasBlockers = computed(() => props.blockers.length > 0);
const singleCollection = computed(
  () => props.tasks.length === 1 && props.tasks[0]?.type === "summary",
);
const dialogTitle = computed(() => {
  if (hasBlockers.value)
    return props.tasks.length === 1
      ? singleCollection.value
        ? "Task collection can’t be deleted"
        : "Task can’t be deleted"
      : "Some selected work can’t be deleted";
  if (props.tasks.length === 1)
    return singleCollection.value ? "Delete task collection?" : "Delete task?";
  return `Delete ${props.tasks.length} work items?`;
});
const keepLabel = computed(() => {
  if (singleCollection.value) return "Keep task collection";
  return props.tasks.length === 1 ? "Keep task" : "Keep selected work";
});
const deleteLabel = computed(() => {
  if (singleCollection.value) return "Delete task collection";
  return props.tasks.length === 1
    ? "Delete task"
    : `Delete ${props.tasks.length} work items`;
});

const cancel = () => {
  if (!props.deleting) emit("cancel");
};

const confirm = () => {
  if (confirmation.value === "delete" && !props.deleting && !hasBlockers.value)
    emit("confirm");
};

function trapKeys(event: KeyboardEvent) {
  if (event.key === "Escape") {
    event.preventDefault();
    cancel();
    return;
  }
  if (event.key !== "Tab" || !dialog.value) return;
  const controls = [
    ...dialog.value.querySelectorAll<HTMLElement>(
      "input:not([disabled]), button:not([disabled])",
    ),
  ];
  if (!controls.length) return;
  if (event.shiftKey && document.activeElement === controls[0]) {
    event.preventDefault();
    controls.at(-1)?.focus();
  } else if (!event.shiftKey && document.activeElement === controls.at(-1)) {
    event.preventDefault();
    controls[0]?.focus();
  }
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      confirmation.value = "";
      opener = document.activeElement as HTMLElement;
      priorBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      await nextTick();
      if (hasBlockers.value) cancelButton.value?.focus();
      else confirmationInput.value?.focus();
      return;
    }
    document.body.style.overflow = priorBodyOverflow;
    opener?.focus();
    opener = null;
  },
  { immediate: true },
);
watch(
  () => hasBlockers.value,
  async (blocked) => {
    if (!props.open || !blocked) return;
    await nextTick();
    cancelButton.value?.focus();
  },
);

onBeforeUnmount(() => {
  if (props.open) document.body.style.overflow = priorBodyOverflow;
});
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="task-delete-layer">
      <button
        class="task-delete-scrim"
        type="button"
        :aria-label="hasBlockers ? 'Close deletion warning' : keepLabel"
        :disabled="deleting"
        @click="cancel"
      ></button>
      <form
        ref="dialog"
        class="task-delete-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="task-delete-title"
        aria-describedby="task-delete-description"
        @keydown="trapKeys"
        @submit.prevent="confirm"
      >
        <header>
          <span
            class="task-delete-dialog__icon"
            :class="{ 'task-delete-dialog__icon--blocked': hasBlockers }"
            aria-hidden="true"
          >
            <AlertTriangle v-if="hasBlockers" :size="20" />
            <Trash2 v-else :size="20" />
          </span>
          <div>
            <h2 id="task-delete-title">{{ dialogTitle }}</h2>
            <p v-if="hasBlockers" id="task-delete-description">
              Remove every child and dependency relationship listed below, then
              try again. Nothing has been deleted.
            </p>
            <p v-else id="task-delete-description">
              This permanently deletes the selected
              {{ tasks.length === 1 ? "work item" : "work items" }}, including
              comments and files. Linked issues and risks remain without a task
              link. No child work or dependency relationships will be removed.
              This cannot be undone.
            </p>
            <p
              v-if="hasUnsavedChanges && !hasBlockers"
              class="task-delete-dialog__warning"
            >
              Unsaved changes in this task panel will also be discarded.
            </p>
          </div>
        </header>
        <div class="task-delete-dialog__body">
          <section
            class="task-delete-dialog__selection"
            aria-labelledby="task-delete-list-title"
          >
            <h3 id="task-delete-list-title">
              {{
                tasks.length === 1
                  ? singleCollection
                    ? "Task collection selected"
                    : "Task selected"
                  : "Work selected"
              }}
            </h3>
            <ul>
              <li v-for="task in tasks" :key="task.id">{{ task.title }}</li>
            </ul>
          </section>
          <section
            v-if="hasBlockers"
            class="task-delete-dialog__blockers"
            aria-labelledby="task-delete-blockers-title"
          >
            <h3 id="task-delete-blockers-title">Remove these relationships</h3>
            <article v-for="blocker in blockers" :key="blocker.taskId">
              <h4>{{ blocker.taskTitle }}</h4>
              <div v-if="blocker.children.length">
                <strong>Contains</strong>
                <ul>
                  <li v-for="title in blocker.children" :key="`child-${title}`">
                    {{ title }}
                  </li>
                </ul>
              </div>
              <div v-if="blocker.blockedBy.length">
                <strong>Blocked by</strong>
                <ul>
                  <li
                    v-for="title in blocker.blockedBy"
                    :key="`predecessor-${title}`"
                  >
                    {{ title }}
                  </li>
                </ul>
              </div>
              <div v-if="blocker.blocking.length">
                <strong>Blocking</strong>
                <ul>
                  <li
                    v-for="title in blocker.blocking"
                    :key="`successor-${title}`"
                  >
                    {{ title }}
                  </li>
                </ul>
              </div>
            </article>
          </section>
          <label
            v-if="!hasBlockers"
            class="field"
            for="task-delete-confirmation"
          >
            <span>Type <strong>delete</strong> to confirm</span>
            <input
              id="task-delete-confirmation"
              ref="confirmationInput"
              v-model="confirmation"
              class="input"
              type="text"
              autocomplete="off"
              autocapitalize="none"
              spellcheck="false"
              :disabled="deleting"
            />
          </label>
          <p v-if="error" class="task-delete-dialog__error" role="alert">
            {{ error }}
          </p>
        </div>
        <footer>
          <button
            ref="cancelButton"
            class="button button--secondary"
            type="button"
            :disabled="deleting"
            @click="cancel"
          >
            {{ hasBlockers ? "Close" : keepLabel }}
          </button>
          <button
            v-if="!hasBlockers"
            class="button button--danger"
            type="submit"
            :disabled="confirmation !== 'delete' || deleting"
          >
            {{ deleting ? "Deleting…" : deleteLabel }}
          </button>
        </footer>
      </form>
    </div>
  </Teleport>
</template>

<style scoped>
.task-delete-layer {
  position: fixed;
  inset: 0;
  z-index: 180;
  display: grid;
  place-items: center;
  padding: 20px;
}
.task-delete-scrim {
  position: absolute;
  inset: 0;
  border: 0;
  background: rgba(13, 35, 50, 0.58);
}
.task-delete-dialog {
  position: relative;
  width: min(560px, 100%);
  max-height: min(680px, calc(100vh - 40px));
  display: grid;
  grid-template-rows: auto minmax(0, 1fr) auto;
  overflow: hidden;
  border-radius: var(--radius-lg);
  background: var(--sheet);
  box-shadow: var(--shadow-panel);
}
.task-delete-dialog > header {
  display: flex;
  gap: 14px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--rule);
}
.task-delete-dialog h2 {
  margin: 0 0 6px;
  color: var(--ink-900);
  font-size: 20px;
  line-height: 1.2;
}
.task-delete-dialog header p {
  max-width: 68ch;
  margin: 0;
  color: var(--text-soft);
}
.task-delete-dialog__icon {
  width: 38px;
  height: 38px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: var(--radius-md);
  background: var(--danger-soft);
  color: var(--danger);
}
.task-delete-dialog__icon--blocked {
  background: var(--warning-soft);
  color: var(--warning);
}
.task-delete-dialog .task-delete-dialog__warning {
  margin-top: 8px;
  color: var(--danger);
  font-weight: 650;
}
.task-delete-dialog__body {
  min-height: 0;
  overflow: auto;
  padding: 18px 24px 22px;
}
.task-delete-dialog__body h3 {
  margin: 0 0 8px;
  color: var(--text);
  font-size: 13px;
}
.task-delete-dialog__selection > ul {
  max-height: 240px;
  margin: 0 0 18px;
  padding: 4px 0;
  overflow: auto;
  list-style: none;
  border-block: 1px solid var(--rule);
}
.task-delete-dialog__selection > ul > li {
  padding: 8px 2px;
  overflow-wrap: anywhere;
  color: var(--text);
}
.task-delete-dialog__selection > ul > li + li {
  border-top: 1px solid var(--rule);
}
.task-delete-dialog__blockers {
  display: grid;
  gap: 10px;
  margin-block: 2px 18px;
}
.task-delete-dialog__blockers article {
  min-width: 0;
  padding: 12px;
  border: 1px solid var(--rule);
  border-radius: var(--radius-md);
  background: var(--warning-soft);
}
.task-delete-dialog__blockers h4 {
  margin: 0 0 8px;
  overflow-wrap: anywhere;
  color: var(--ink-900);
  font-size: 14px;
}
.task-delete-dialog__blockers article > div {
  display: grid;
  grid-template-columns: minmax(82px, auto) minmax(0, 1fr);
  gap: 10px;
  padding-block: 5px;
}
.task-delete-dialog__blockers strong {
  color: var(--warning);
  font-size: 12px;
}
.task-delete-dialog__blockers ul {
  display: grid;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.task-delete-dialog__blockers li {
  overflow-wrap: anywhere;
  color: var(--text);
}
.task-delete-dialog .field {
  display: grid;
  gap: 7px;
}
.task-delete-dialog__error {
  margin: 12px 0 0;
  padding: 10px 12px;
  border-radius: var(--radius-sm);
  background: var(--danger-soft);
  color: var(--danger);
}
.task-delete-dialog > footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid var(--rule);
  background: var(--sheet-subtle);
}
@media (max-width: 520px) {
  .task-delete-layer {
    align-items: end;
    padding: 0;
  }
  .task-delete-dialog {
    width: 100%;
    max-height: calc(100dvh - 20px);
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  }
  .task-delete-dialog > header,
  .task-delete-dialog__body,
  .task-delete-dialog > footer {
    padding-inline: 18px;
  }
  .task-delete-dialog > footer {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  .task-delete-dialog > footer > :only-child {
    grid-column: 1 / -1;
    justify-self: end;
  }
  .task-delete-dialog__blockers article > div {
    grid-template-columns: 1fr;
    gap: 3px;
  }
  .task-delete-dialog .input {
    font-size: 16px;
  }
}
</style>
